import "./App.css";
import { Route, Routes } from "react-router-dom";
import NotFound from "./components/errorpage";
import PublicRoutes from "./routes/PublicRoutes";
import PrivateRoutes from "./routes/PrivateRoutes";
import { lazy, Suspense } from "react";
import { PageLoader } from "./pages/roleManagement/getRole/roleManagementStyle";
import PageLoaderIcon from "./assets/images/page-loader.svg";
import 'react-toastify/dist/ReactToastify.css';

// Lazy load the components

const Login = lazy(() => import("./pages/auth/Login"));
const Dashboard = lazy(() => import("./components/dashboard"));
const ForgotPassword = lazy(() => import("./pages/auth/forgotPassword"));
const ResetPassword = lazy(() => import("./pages/auth/resetPassword"));
const UserProfile = lazy(() => import("./pages/userProfile"));
const RoleManagement = lazy(() => import("./pages/roleManagement/getRole"));
const AddRole = lazy(() => import("./pages/roleManagement/addRole"));
const EditRole = lazy(() => import("./pages/roleManagement/editRole"));
const AddPermission = lazy(() => import("./pages/roleManagement/addPermission"));
const CustomerManagement = lazy(() => import("./pages/customerManagement/getCustomer"));
const AddCustomer = lazy(() => import("./pages/customerManagement/addCustomer"));
const EditCustomer = lazy(() => import("./pages/customerManagement/editCustomer"));
const CustomerDetail = lazy(() => import("./pages/customerManagement/customerDetails/index"));
const StaffManagement = lazy(() => import("./pages/staffManagement/ListOfStaff"));
const AddStaff = lazy(() => import("./pages/staffManagement/AddStaff"));
const EditStaffDetail = lazy(() => import("./pages/staffManagement/EditStaff"));
const StaffDetail = lazy(() => import("./pages/staffManagement/staffDetails"));
const BookingManagement = lazy(() => import("./pages/bookingManagement/getBooking"));
const EditBooking = lazy(() => import("./pages/bookingManagement/editBooking"));
const AddBooking = lazy(() => import("./pages/bookingManagement/addBooking"));
const CloneBooking = lazy(() => import("./pages/bookingManagement/cloneBooking"));
const GetBooking = lazy(() => import("./pages/customerManagement/getBooking"));
const BookingDetail = lazy(() => import("./pages/bookingManagement/bookingDetails"));
const ChefManagementList = lazy(() => import("./pages/chefManagement/ListOfChefs"));
const ChefDetail = lazy(() => import("./pages/chefManagement/ViewChefsDetail"));
const AddEditChef = lazy(() => import("./pages/chefManagement/AddEditChef"));
const CuisineManagement = lazy(() => import("./pages/CuisineManagement/getCuisine"));
const AddCuisine = lazy(() => import("./pages/CuisineManagement/addCuisine"));
const EditCuisine = lazy(() => import("./pages/CuisineManagement/editCuisine"));
const CuisinesDetail = lazy(() => import("./pages/CuisineManagement/cuisineDetails"));
const DishesManagement = lazy(() => import("./pages/dishesManagement/getDishes"));
const AddDishes = lazy(() => import("./pages/dishesManagement/addDishes"));
const DishesDetail = lazy(() => import("./pages/dishesManagement/dishesDetails"));
const EditDishes = lazy(() => import("./pages/dishesManagement/editDishes"));
const RatingReviewManagement = lazy(() => import("./pages/ratingreviewManagement/ratingReviewDetails"));
const RatingReviewmanageDetail = lazy(() => import("./pages/ratingreviewManagement/RatingReviewmanageDetail"));
const ContactManagement = lazy(() => import("./pages/contactManagement/getContact"));
const ContactDetail = lazy(() => import("./pages/contactManagement/contactDetails"));
const CouponsDetail = lazy(() => import("./pages/couponsManagement/couponsDetails"));
const CouponsManagement = lazy(() => import("./pages/couponsManagement/getCoupons"));
const AddCoupons = lazy(() => import("./pages/couponsManagement/addCoupons"));
const EditCoupons = lazy(() => import("./pages/couponsManagement/editCoupons"));
const NotificationManagement = lazy(() => import("./pages/notification-management"));
const Notification = lazy(() => import("./pages/notifications"));
const ConfigManagement = lazy(() => import("./pages/config-management"));
const Reports = lazy(() => import("./pages/reports"));
const TransactionManagement = lazy(() => import("./pages/transcationManagement/getTranscation"));
const TransactionDetail = lazy(() => import("./pages/transcationManagement/transcationDetails"));
const ChatManagement = lazy(() => import("./pages/chatManagement"));
const CMSManagement = lazy(() => import("./pages/cms-management"));
const CustomerSupport = lazy(() => import("./pages/chatManagement/customerSupport"));
const DishConfigManagement = lazy(() => import("./pages/dish-config-management"));
const RevenueManagement = lazy(() => import("./pages/revenueManagement/getRevenue"));

export const isAuthenticated = () => {
  let token = localStorage.getItem("token");
  if (token) {
    sessionCreated();
    return true;
  }
  return false;
}

const sessionCreated = () => {
  const sessionStartTime = new Date().getTime();
  localStorage.setItem('sessionStartTime', sessionStartTime.toString());
  const timer = setTimeout(() => {
    window.location.reload();
    localStorage.clear();
  }, 24 * 60 * 60 * 1000);
  return () => clearTimeout(timer)
}

const App = () => {

  return (
    <Suspense fallback={<PageLoader><img src={PageLoaderIcon} alt="Loading..." /></PageLoader>}>
    <Routes>
      <Route path="/dish-config" element={<PrivateRoutes />}>
        <Route path="list" element={<DishConfigManagement list />} />
        <Route path="add" element={<DishConfigManagement add />} />
        <Route path="edit/:id" element={<DishConfigManagement edit />} />
        <Route path=":id" element={<DishConfigManagement view />} />
      </Route>

      <Route path="/cms-management" element={<PrivateRoutes />}>
        <Route path="list" element={<CMSManagement list />} />
        <Route path="add" element={<CMSManagement add />} />
        <Route path="edit/:id" element={<CMSManagement edit />} />
        <Route path=":id" element={<CMSManagement view />} />
      </Route>

      <Route path="/notification-management" element={<PrivateRoutes />}>
        <Route path="list" element={<NotificationManagement list />} />
        <Route path="add" element={<NotificationManagement add />} />
        <Route path=":id" element={<NotificationManagement view />} />
      </Route>

      <Route path="/platform-fee" element={<PrivateRoutes />}>
        <Route path="/platform-fee" element={<ConfigManagement />} />
      </Route>

      <Route path="/" element={<PublicRoutes />}>
        <Route path="/" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password/:token" element={<ResetPassword />} />
      </Route>
      <Route path="/" element={<PrivateRoutes />}>
        <Route path="/dashboard" element={<Dashboard />} />
      </Route>
      
      <Route path="/user-profile" element={<PrivateRoutes />}>
        <Route path="/user-profile" element={<UserProfile />} />
      </Route>

      {/* Role Routes : */}
      <Route path="/role-management" element={<PrivateRoutes />}>
        <Route path="/role-management" element={<RoleManagement />} />
      </Route>
      <Route path="/addRole" element={<PrivateRoutes />}>
        <Route path="/addRole" element={<AddRole />} />
      </Route>
      <Route path="/editRole/:id" element={<PrivateRoutes />}>
        <Route path="/editRole/:id" element={<EditRole />} />
      </Route>
      <Route path="/addPermission" element={<PrivateRoutes />}>
        <Route path="/addPermission/:id" element={<AddPermission />} />
      </Route>

      {/* customer Routes : */}
      <Route path="/customer-management" element={<PrivateRoutes />}>
        <Route path="/customer-management" element={<CustomerManagement />} />
      </Route>
      <Route path="/addCustomer" element={<PrivateRoutes />}>
        <Route path="/addCustomer" element={<AddCustomer />} />
      </Route>
      <Route path="/editCustomer/:id" element={<PrivateRoutes />}>
        <Route path="/editCustomer/:id" element={<EditCustomer />} />
      </Route>
      <Route path="/customerDetail/:id" element={<PrivateRoutes />}>
        <Route path="/customerDetail/:id" element={<CustomerDetail />} />
      </Route>

      {/* Staff Routes : */}
      <Route path="/staff-management" element={<PrivateRoutes />}>
        <Route path="/staff-management" element={<StaffManagement />} />
      </Route>
      <Route path="/staff-management/add" element={<PrivateRoutes />}>
        <Route path="/staff-management/add" element={<AddStaff />} />
      </Route>
      <Route path="/staff-management/edit/:id" element={<PrivateRoutes />}>
        <Route path="/staff-management/edit/:id" element={<EditStaffDetail />} />
      </Route>
      <Route path="/staffDetail/:id" element={<PrivateRoutes />}>
        <Route path="/staffDetail/:id" element={<StaffDetail />} />
      </Route>

      {/* Booking Routes : */}
      <Route path="/getBooking/:id" element={<PrivateRoutes />}>
        <Route path="/getBooking/:id" element={<GetBooking />} />
      </Route>
      <Route path="/booking-management" element={<PrivateRoutes />}>
        <Route path="/booking-management" element={<BookingManagement />} />
      </Route>
      <Route path="/bookingDetail/:id" element={<PrivateRoutes />}>
        <Route path="/bookingDetail/:id" element={<BookingDetail />} />
      </Route>
      <Route path="/editBooking/:id" element={<PrivateRoutes />}>
        <Route path="/editBooking/:id" element={<EditBooking />} />
      </Route>
      <Route path="/cloneBooking/:id" element={<PrivateRoutes />}>
        <Route path="/cloneBooking/:id" element={<CloneBooking />} />
      </Route>
      <Route path="/add-booking" element={<PrivateRoutes />}>
        <Route path="/add-booking" element={<AddBooking />} />
      </Route>

      {/* Chef Routes : */}
      <Route path="/chef-management" element={<PrivateRoutes />}>
        <Route path="/chef-management" element={<ChefManagementList />} />
      </Route>
      <Route path="/chef-management/add" element={<PrivateRoutes />}>
        <Route path="/chef-management/add" element={<AddEditChef />} />
      </Route>
      <Route path="/chef-management/edit/:id" element={<PrivateRoutes />}>
        <Route path="/chef-management/edit/:id" element={<AddEditChef edit={true} />} />
      </Route>
      <Route path="/chef-management/view/:id" element={<PrivateRoutes />}>
        <Route path="/chef-management/view/:id" element={<ChefDetail />} />
      </Route>

      {/* Cuisine Routes : */}
      <Route path="/cuisine-management" element={<PrivateRoutes />}>
        <Route path="/cuisine-management" element={<CuisineManagement />} />
      </Route>
      <Route path="/addCuisine" element={<PrivateRoutes />}>
        <Route path="/addCuisine" element={<AddCuisine />} />
      </Route>
      <Route path="/editCuisine" element={<PrivateRoutes />}>
        <Route path="/editCuisine/:id" element={<EditCuisine />} />
      </Route>
      <Route path="/CuisinesDetail" element={<PrivateRoutes />}>
        <Route path="/CuisinesDetail/:id" element={<CuisinesDetail />} />
      </Route>

      {/* Dishes Routes : */}

      <Route path="/dishes-management" element={<PrivateRoutes />}>
        <Route path="/dishes-management" element={<DishesManagement />} />
      </Route>
      <Route path="/add-dishes" element={<PrivateRoutes />}>
        <Route path="/add-dishes" element={<AddDishes />} />
      </Route>
      <Route path="/add-dishes" element={<PrivateRoutes />}>
        <Route path="/add-dishes/:id" element={<EditDishes />} />
      </Route>
      <Route path="/dishesDetail" element={<PrivateRoutes />}>
        <Route path="/dishesDetail/:id" element={<DishesDetail />} />
      </Route>
      <Route path="/editDishes" element={<PrivateRoutes />}>
        <Route path="/editDishes/:id" element={<EditDishes />} />
      </Route>

      {/* Rating & Riview Routes : */}

      <Route path="/rating-review" element={<PrivateRoutes />}>
        <Route path="/rating-review" element={<RatingReviewManagement />} />
      </Route>

      <Route path="/ratingreviewDetail/:id" element={<PrivateRoutes />}>
        <Route path="/ratingreviewDetail/:id" element={<RatingReviewmanageDetail />} />
      </Route>

      {/* Contact Routes : */}

      <Route path="/contact-management" element={<PrivateRoutes />}>
        <Route path="/contact-management" element={<ContactManagement />} />
      </Route>

      <Route path="/contactDetail/:id" element={<PrivateRoutes />}>
        <Route path="/contactDetail/:id" element={<ContactDetail />} />
      </Route>

      {/* Coupons Routes : */}


      <Route path="/coupons-management" element={<PrivateRoutes />}>
        <Route path="/coupons-management" element={<CouponsManagement />} />
      </Route>

      <Route path="/couponsDetail/:id" element={<PrivateRoutes />}>
        <Route path="/couponsDetail/:id" element={<CouponsDetail />} />
      </Route>

      <Route path="/add-coupons" element={<PrivateRoutes />}>
        <Route path="/add-coupons" element={<AddCoupons />} />
      </Route>
      <Route path="/edit-coupons" element={<PrivateRoutes />}>
        <Route path="/edit-coupons/:id" element={<EditCoupons />} />
      </Route>

      {/* notification Routes : */}

      <Route path="/notification" element={<PrivateRoutes />}>
        <Route path="/notification" element={<Notification />} />
      </Route>
      
      {/* Reports Routes : */}
      
      <Route path="/reports" element={<PrivateRoutes />}>
        <Route path="/reports" element={<Reports />} />
      </Route>
      
      {/* Transaction Routes : */}
      <Route path="/transaction-management" element={<PrivateRoutes />}>
        <Route path="/transaction-management" element={<TransactionManagement />} />
      </Route>

      <Route path="/transactionDetail/:id" element={<PrivateRoutes />}>
        <Route path="/transactionDetail/:id" element={<TransactionDetail />} />
      </Route>

      {/* Revenue Routes : */}
      <Route path="/revenue-management" element={<PrivateRoutes />}>
        <Route path="/revenue-management" element={<RevenueManagement />} />
      </Route>

      {/* Chat Routes : */}
      <Route path="/chats" element={<PrivateRoutes />}>
        <Route path="/chats" element={<ChatManagement />} />
      </Route>

      {/* Customer Support Routes : */}
      <Route path="/support-chats" element={<PrivateRoutes />}>
        <Route path="/support-chats" element={<CustomerSupport />} />
      </Route>

      <Route path="*" element={<NotFound />} />
    </Routes>
    </Suspense>
  );
}
export default App;
